<template>
  <li class="play-nav-item">
    <component
      :is="resolvedTag"
      class="play-nav-item__link"
      :class="{ 'is-active': active, 'is-disabled': disabled }"
      :to="resolvedTag === 'router-link' ? to : null"
      :href="resolvedTag === 'a' ? (disabled ? null : href || '#') : null"
      :type="resolvedTag === 'button' ? 'button' : null"
      :aria-current="active ? 'page' : null"
      :aria-disabled="disabled ? 'true' : null"
      v-bind="$attrs"
      v-on="$listeners"
    >
      <!-- VUE3-MIGRATION: $listeners removed in Vue 3 (merged into $attrs).
         On migration delete `v-on="$listeners"` — v-bind="$attrs" forwards listeners too. -->
      <slot />
    </component>
  </li>
</template>

<script>
/**
 * PlayNavItem — a single item inside <PlayNav>.
 *
 * Token-driven (see src/styles/tokens.scss). Replaces bootstrap-vue's
 * <b-nav-item>. Renders a <router-link> (`to`), <a> (`href`), or <button>
 * (neither) link with hover + active states.
 *
 * VUE3-MIGRATION: `v-on="$listeners"` is the only Vue 2-ism; drop it on Vue 3
 * (v-bind="$attrs" keeps forwarding listeners). Props/slots are Vue 3-safe.
 */
export default {
  name: 'PlayNavItem',
  inheritAttrs: false,
  props: {
    to: {
      type: [String, Object],
      default: null,
    },
    href: {
      type: String,
      default: '',
    },
    active: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    resolvedTag() {
      if (this.to) return 'router-link'
      if (this.href) return 'a'
      return 'button'
    },
  },
}
</script>

<style lang="scss" scoped>
.play-nav-item {
  display: flex;

  &__link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 9px 14px;
    font-family: var(--font-display);
    font-weight: 700;
    font-size: 15px;
    color: var(--text-body);
    text-decoration: none;
    background: transparent;
    border: 0;
    border-radius: var(--radius-sm);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out);

    &:hover:not(.is-disabled) {
      background: var(--c-secondary-soft);
      color: var(--c-secondary-strong);
    }

    &.is-active {
      background: var(--c-secondary-soft);
      color: var(--c-secondary-strong);
    }

    &.is-disabled {
      opacity: .5;
      pointer-events: none;
    }

    &:focus-visible { outline: 2px solid var(--c-secondary); outline-offset: 2px; }
  }
}

@media (prefers-reduced-motion: reduce) {
  .play-nav-item__link { transition: none; }
}
</style>
